import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { entityAPI, transactionAPI } from '../lib/api';
import { formatDate, formatRelativeTime, formatCurrency, getRiskClass, getRiskColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  User,
  Building,
  Wallet,
  CreditCard,
  Globe,
  Calendar,
  Activity,
  AlertTriangle,
  ArrowRight,
  Shield,
  FileText,
  Network,
  RefreshCw,
  ExternalLink,
  Flag,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const ENTITY_ICONS = {
  person: User,
  company: Building,
  wallet: Wallet,
  account: CreditCard,
};

// Entity List Component
function EntityList() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const response = await entityAPI.list({ limit: 100 });
      setEntities(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load entities');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
        Entity Management
      </h1>
      <Card className="glass">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Entity</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entities.map((entity) => {
                const Icon = ENTITY_ICONS[entity.entity_type] || User;
                return (
                  <TableRow 
                    key={entity.id} 
                    className="cursor-pointer hover:bg-white/5"
                    onClick={() => navigate(`/entities/${entity.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                        <span className="text-white font-medium">{entity.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{entity.entity_type}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getRiskClass(entity.risk_level)}>
                        {entity.risk_level}
                      </Badge>
                    </TableCell>
                    <TableCell>{entity.country || '-'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// Entity Detail Component
function EntityDetail({ entityId }) {
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [networkData, setNetworkData] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEntityDetails();
  }, [entityId]);

  const fetchEntityDetails = async () => {
    setLoading(true);
    try {
      const [entityRes, txnRes, networkRes] = await Promise.all([
        entityAPI.get(entityId),
        entityAPI.getTransactions(entityId, 20),
        entityAPI.getNetwork(entityId, 1)
      ]);
      
      setEntity(entityRes.data);
      setTransactions(txnRes.data || []);
      setNetworkData(networkRes.data || { nodes: [], edges: [] });
    } catch (error) {
      toast.error('Failed to load entity details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Entity not found</p>
        <Button variant="outline" onClick={() => navigate('/entities')} className="mt-4">
          Back to Entities
        </Button>
      </div>
    );
  }

  const EntityIcon = ENTITY_ICONS[entity.entity_type] || User;
  const riskColor = getRiskColor(entity.risk_level);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="entity-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/entities')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-2">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${riskColor}20` }}
            >
              <EntityIcon className="w-6 h-6" style={{ color: riskColor }} />
            </div>
            <div>
              <h1 className="font-secondary text-2xl font-bold text-white">
                {entity.name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <Badge variant="outline" className="text-xs capitalize">
                  {entity.entity_type}
                </Badge>
                <Badge 
                  variant="outline" 
                  className={`${getRiskClass(entity.risk_level)} text-xs uppercase`}
                >
                  {entity.risk_level} Risk
                </Badge>
                {entity.pep_status && (
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                    PEP
                  </Badge>
                )}
                {entity.sanctions_status && (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                    SANCTIONED
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/network?entity=${entityId}`}>
            <Button variant="outline" className="gap-2">
              <Network className="w-4 h-4" />
              View Network
            </Button>
          </Link>
          <Button className="gap-2" data-testid="create-case-btn">
            <FileText className="w-4 h-4" />
            Create Case
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{entity.risk_score?.toFixed(0) || 0}</p>
                <p className="text-xs text-muted-foreground">Risk Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <Activity className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{transactions.length}</p>
                <p className="text-xs text-muted-foreground">Transactions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Network className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{networkData.nodes.length}</p>
                <p className="text-xs text-muted-foreground">Connections</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {transactions.filter(t => t.is_flagged).length}
                </p>
                <p className="text-xs text-muted-foreground">Flagged TXNs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="glass">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="transactions">Transactions ({transactions.length})</TabsTrigger>
          <TabsTrigger value="connections">Connections ({networkData.nodes.length})</TabsTrigger>
          <TabsTrigger value="activity">Activity Timeline</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Basic Info */}
            <Card className="glass lg:col-span-2">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                  Entity Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="label-mono">Entity Type</p>
                  <p className="text-white capitalize">{entity.entity_type}</p>
                </div>
                <div>
                  <p className="label-mono">Country</p>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <p className="text-white">{entity.country || 'Unknown'}</p>
                  </div>
                </div>
                <div>
                  <p className="label-mono">Created</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <p className="text-white">{formatDate(entity.created_at)}</p>
                  </div>
                </div>
                <div>
                  <p className="label-mono">Last Active</p>
                  <p className="text-white">{formatRelativeTime(entity.last_active)}</p>
                </div>
                {entity.aliases?.length > 0 && (
                  <div className="col-span-2">
                    <p className="label-mono">Known Aliases</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {entity.aliases.map((alias, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {alias}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {entity.industry && (
                  <div>
                    <p className="label-mono">Industry</p>
                    <p className="text-white">{entity.industry}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Risk Factors */}
            <Card className="glass">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                  Risk Factors
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <span className="text-sm text-muted-foreground">Risk Score</span>
                  <span 
                    className="text-lg font-bold"
                    style={{ color: riskColor }}
                  >
                    {entity.risk_score?.toFixed(0) || 0}/100
                  </span>
                </div>
                
                {entity.details?.pep_connections && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <Flag className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-purple-300">PEP Connections</span>
                  </div>
                )}
                
                {entity.details?.offshore_accounts && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <Globe className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-orange-300">Offshore Accounts</span>
                  </div>
                )}
                
                {entity.details?.shell_company_indicators && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-red-300">Shell Company Indicators</span>
                  </div>
                )}
                
                {entity.details?.mixer_usage && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <Wallet className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-red-300">Crypto Mixer Usage</span>
                  </div>
                )}

                {entity.details?.beneficial_owner_unknown && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <User className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-yellow-300">Unknown Beneficial Owner</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card className="glass">
            <CardContent className="p-0">
              {transactions.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reference</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Counterparty</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Risk</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow key={txn.id} className={txn.is_flagged ? 'bg-red-500/5' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {txn.is_flagged && <AlertTriangle className="w-4 h-4 text-red-400" />}
                            <span className="font-mono text-xs">{txn.transaction_ref}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs uppercase">
                            {txn.transaction_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-white">
                            {txn.sender_id === entityId ? txn.receiver_name : txn.sender_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {txn.sender_id === entityId ? 'Outbound' : 'Inbound'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <span className={`font-mono ${txn.sender_id === entityId ? 'text-red-400' : 'text-green-400'}`}>
                            {txn.sender_id === entityId ? '-' : '+'}{formatCurrency(txn.amount, txn.currency)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs">{txn.risk_score || 0}</span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatRelativeTime(txn.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Link to={`/transactions/${txn.id}`}>
                            <Button variant="ghost" size="icon">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Activity className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No transactions found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Connections Tab */}
        <TabsContent value="connections">
          <Card className="glass">
            <CardContent className="p-0">
              {networkData.nodes.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entity</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Risk Level</TableHead>
                      <TableHead>Relationship</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {networkData.nodes
                      .filter(n => n.id !== entityId)
                      .map((node) => {
                        const edge = networkData.edges.find(
                          e => e.source === node.id || e.target === node.id
                        );
                        const NodeIcon = ENTITY_ICONS[node.type] || User;
                        return (
                          <TableRow key={node.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <NodeIcon className="w-5 h-5 text-muted-foreground" />
                                <span className="text-white font-medium">{node.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="capitalize">{node.type}</TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={`${getRiskClass(node.risk_level)} text-xs uppercase`}
                              >
                                {node.risk_level}
                              </Badge>
                            </TableCell>
                            <TableCell className="capitalize text-muted-foreground">
                              {edge?.type?.replace('_', ' ') || 'Connected'}
                            </TableCell>
                            <TableCell>
                              <Link to={`/entities/${node.id}`}>
                                <Button variant="ghost" size="icon">
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Network className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No connections found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card className="glass">
            <CardContent className="p-4">
              <div className="space-y-4">
                {/* Mock activity timeline */}
                <div className="relative pl-6 pb-4 border-l border-white/10">
                  <div className="absolute -left-1.5 top-0 w-3 h-3 rounded-full bg-primary" />
                  <p className="text-sm text-white">Entity created</p>
                  <p className="text-xs text-muted-foreground">{formatDate(entity.created_at)}</p>
                </div>
                {transactions.slice(0, 5).map((txn, i) => (
                  <div key={txn.id} className="relative pl-6 pb-4 border-l border-white/10">
                    <div className={`absolute -left-1.5 top-0 w-3 h-3 rounded-full ${txn.is_flagged ? 'bg-red-500' : 'bg-cyan-500'}`} />
                    <p className="text-sm text-white">
                      Transaction {txn.transaction_ref}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(txn.amount, txn.currency)} • {formatRelativeTime(txn.timestamp)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Main Component
export default function Entities() {
  const { id } = useParams();
  
  if (id) {
    return <EntityDetail entityId={id} />;
  }
  
  return <EntityList />;
}
